(() => {
  const CLAVE = 'mellinas_garaje';
  const reducirMovimiento = () => matchMedia('(prefers-reduced-motion: reduce)').matches;
  const esMovil = () => matchMedia('(max-width: 749px)').matches;

  // el coche viene de localStorage: nos fiamos solo de strings con forma esperada
  const cocheValido = (c) =>
    c && ['marca', 'modelo', 'gen', 'etiqueta', 'token'].every((k) => typeof c[k] === 'string' && c[k]);

  const leer = () => {
    try {
      const c = JSON.parse(localStorage.getItem(CLAVE));
      return cocheValido(c) ? c : null;
    } catch {
      return null;
    }
  };

  const guardar = (coche) => {
    try {
      coche ? localStorage.setItem(CLAVE, JSON.stringify(coche)) : localStorage.removeItem(CLAVE);
    } catch {}
    document.dispatchEvent(new CustomEvent('garaje:cambio', { detail: coche }));
  };

  let promesaCatalogo;
  const cargarCatalogo = (url) =>
    (promesaCatalogo ??= fetch(url)
      .then((r) => r.json())
      .catch(() => ({ marcas: [] })));

  // lee la curva de muelle de los tokens; si el navegador no soporta linear() cae a ease-out
  const muelle = (nombre) => {
    const valor = getComputedStyle(document.documentElement).getPropertyValue(nombre).trim();
    return valor && CSS.supports('transition-timing-function', valor) ? valor : 'ease-out';
  };

  const urlFiltrada = (coche, filtro, coleccion) => {
    const enColeccion = location.pathname.includes('/collections/');
    const url = new URL(enColeccion ? location.pathname : coleccion, location.origin);
    if (enColeccion) new URLSearchParams(location.search).forEach((v, k) => url.searchParams.append(k, v));
    url.searchParams.delete('page');
    url.searchParams.set(filtro, coche.token);
    return url.toString();
  };

  class GarajeSelector extends HTMLElement {
    async connectedCallback() {
      this.formulario = this.querySelector('form');
      this.campos = Object.fromEntries(['marca', 'modelo', 'anio'].map((n) => [n, this.querySelector(`[name=${n}]`)]));
      this.boton = this.querySelector('[type=submit]');
      this.catalogo = await cargarCatalogo(this.dataset.catalogo);

      this.rellenar('marca', this.catalogo.marcas.map((m) => [m.id, m.nombre]), 'Marca');
      this.campos.marca.addEventListener('change', () => this.alCambiarMarca());
      this.campos.modelo.addEventListener('change', () => this.alCambiarModelo());
      this.campos.anio.addEventListener('change', () => (this.boton.disabled = !this.campos.anio.value));
      this.formulario.addEventListener('submit', (e) => this.enviar(e));

      const coche = leer();
      if (coche) this.restaurar(coche);
    }

    rellenar(campo, opciones, titulo) {
      const select = this.campos[campo];
      select.replaceChildren(new Option(titulo, ''), ...opciones.map(([v, t]) => new Option(t, v)));
      select.disabled = opciones.length === 0;
    }

    marcaActual() {
      return this.catalogo.marcas.find((m) => m.id === this.campos.marca.value);
    }

    modeloActual() {
      return this.marcaActual()?.modelos.find((m) => m.id === this.campos.modelo.value);
    }

    alCambiarMarca() {
      const modelos = this.marcaActual()?.modelos ?? [];
      this.rellenar('modelo', modelos.map((m) => [m.id, m.nombre]), 'Modelo');
      this.rellenar('anio', [], 'Año');
      this.boton.disabled = true;
    }

    alCambiarModelo() {
      const gens = this.modeloActual()?.gens ?? [];
      const actual = new Date().getFullYear();
      const anios = new Set();
      gens.forEach((g) => {
        for (let a = g.desde; a <= (g.hasta ?? actual); a++) anios.add(a);
      });
      const lista = [...anios].sort((a, b) => b - a).map((a) => [String(a), String(a)]);
      this.rellenar('anio', lista, 'Año');
      this.boton.disabled = true;
    }

    restaurar(coche) {
      const { marcas } = this.catalogo;
      const marca = marcas.find((m) => coche.token.startsWith(`${m.id}-`));
      const modelo = marca?.modelos.find((m) => coche.token.startsWith(`${marca.id}-${m.id}-`));
      if (!modelo) return;
      this.campos.marca.value = marca.id;
      this.alCambiarMarca();
      this.campos.modelo.value = modelo.id;
      this.alCambiarModelo();
      if (coche.anio) this.campos.anio.value = coche.anio;
      this.boton.disabled = !this.campos.anio.value;
    }

    enviar(e) {
      e.preventDefault();
      const marca = this.marcaActual();
      const modelo = this.modeloActual();
      const anio = Number(this.campos.anio.value);
      const gen = modelo?.gens.find((g) => anio >= g.desde && anio <= (g.hasta ?? 9999));
      if (!gen) return;

      const coche = {
        marca: marca.id,
        modelo: modelo.id,
        gen: gen.id,
        anio: String(anio),
        etiqueta: `${marca.nombre} ${modelo.nombre} ${anio}`,
        token: `${marca.id}-${modelo.id}-${gen.id}`,
      };
      guardar(coche);
      location.href = urlFiltrada(coche, this.dataset.filtro, this.dataset.coleccion);
    }
  }

  class GarajeHoja extends HTMLElement {
    connectedCallback() {
      this.dialogo = this.querySelector('dialog');
      this.panel = this.querySelector('[data-panel]');
      this.asa = this.querySelector('[data-asa]');

      document.addEventListener('click', (e) => {
        if (e.target.closest('[data-garaje-abrir]')) this.abrir(e.target.closest('[data-garaje-abrir]'));
      });
      this.dialogo.addEventListener('click', (e) => e.target === this.dialogo && this.cerrar());
      this.dialogo.addEventListener('cancel', (e) => {
        e.preventDefault();
        this.cerrar();
      });
      this.querySelector('[data-cerrar]').addEventListener('click', () => this.cerrar());
      this.querySelector('[data-ver-piezas]').addEventListener('click', () => {
        const coche = leer();
        if (coche) location.href = urlFiltrada(coche, this.dataset.filtro, this.dataset.coleccion);
      });
      this.querySelector('[data-quitar]').addEventListener('click', () => {
        guardar(null);
        location.href = this.dataset.coleccion;
      });
      document.addEventListener('garaje:cambio', () => this.pintarEstado());
      this.pintarEstado();
      this.hacerArrastrable();
    }

    pintarEstado() {
      const coche = leer();
      this.querySelector('[data-con-coche]').hidden = !coche;
      if (coche) this.querySelector('[data-etiqueta-coche]').textContent = coche.etiqueta;
    }

    // en móvil sube desde abajo; en escritorio nace del botón que la abrió (origen anclado)
    abrir(origen) {
      if (this.dialogo.open) return;
      this.origen = origen;
      this.dialogo.showModal();
      if (reducirMovimiento()) return;

      if (esMovil()) {
        this.panel.animate([{ transform: 'translateY(100%)' }, { transform: 'translateY(0)' }], {
          duration: 600,
          easing: muelle('--muelle-suave'),
        });
      } else {
        const r = origen.getBoundingClientRect();
        const p = this.panel.getBoundingClientRect();
        this.panel.style.transformOrigin = `${r.left + r.width / 2 - p.left}px ${r.bottom - p.top}px`;
        this.panel.animate(
          [
            { transform: 'scale(0.92)', opacity: 0 },
            { transform: 'scale(1)', opacity: 1 },
          ],
          { duration: 500, easing: muelle('--muelle-suave') }
        );
      }
    }

    cerrar(desde = 0, velocidad = 0) {
      if (!this.dialogo.open) return;
      if (reducirMovimiento()) return this.dialogo.close();
      const alto = this.panel.getBoundingClientRect().height;
      const destino = esMovil() ? `translateY(${alto}px)` : 'scale(0.94)';
      const origenAnim = esMovil() ? `translateY(${desde}px)` : 'scale(1)';
      // si el usuario soltó con inercia, la salida dura menos
      const duracion = Math.max(160, 320 - Math.min(Math.abs(velocidad) / 8, 160));
      const anim = this.panel.animate(
        [
          { transform: origenAnim, opacity: 1 },
          { transform: destino, opacity: esMovil() ? 1 : 0 },
        ],
        { duration: duracion, easing: 'ease-in', fill: 'forwards' }
      );
      anim.finished.then(() => {
        this.dialogo.close();
        anim.cancel();
      }, () => {});
    }

    // arrastrar la asa hacia abajo: 1:1 con el dedo, y al soltar decide con la velocidad proyectada
    hacerArrastrable() {
      let y = 0;
      let historial = [];
      let activo = false;

      const yActual = () => {
        const m = new DOMMatrixReadOnly(getComputedStyle(this.panel).transform);
        return m.m42;
      };

      this.asa.addEventListener('pointerdown', (e) => {
        if (!esMovil()) return;
        // interrumpible: si estaba animando, arrancamos desde donde está ahora mismo
        const enVuelo = this.panel.getAnimations().length > 0;
        y = enVuelo ? yActual() : 0;
        this.panel.getAnimations().forEach((a) => a.cancel());
        this.panel.style.transform = `translateY(${y}px)`;
        this.inicioY = e.clientY - y;
        historial = [{ t: e.timeStamp, y: e.clientY }];
        activo = true;
        this.asa.setPointerCapture(e.pointerId);
      });

      this.asa.addEventListener('pointermove', (e) => {
        if (!activo) return;
        const dy = e.clientY - this.inicioY;
        // hacia arriba resiste (rubber-band), hacia abajo sigue al dedo
        y = dy >= 0 ? dy : -Math.sqrt(-dy) * 2.2;
        this.panel.style.transform = `translateY(${y}px)`;
        historial.push({ t: e.timeStamp, y: e.clientY });
        if (historial.length > 6) historial.shift();
      });

      const soltar = () => {
        if (!activo) return;
        activo = false;
        const primero = historial[0];
        const ultimo = historial[historial.length - 1];
        const velocidad = ultimo.t > primero.t ? ((ultimo.y - primero.y) / (ultimo.t - primero.t)) * 1000 : 0;
        // proyección de inercia como la de apple: dónde acabaría si lo dejáramos deslizar
        const proyectado = y + (velocidad / 1000) * (0.998 / (1 - 0.998));
        const alto = this.panel.getBoundingClientRect().height;
        this.panel.style.transform = '';

        if (proyectado > alto * 0.5) {
          this.cerrar(y, velocidad);
        } else {
          this.panel.animate([{ transform: `translateY(${y}px)` }, { transform: 'translateY(0)' }], {
            duration: 600,
            easing: muelle('--muelle-firme'),
          });
        }
      };
      this.asa.addEventListener('pointerup', soltar);
      this.asa.addEventListener('pointercancel', soltar);
    }
  }

  customElements.define('garaje-selector', GarajeSelector);
  customElements.define('garaje-hoja', GarajeHoja);

  // etiqueta del coche en la píldora de la cabecera
  const pintarPildoras = () => {
    const coche = leer();
    document.querySelectorAll('[data-garaje-etiqueta]').forEach((el) => {
      el.textContent = coche ? coche.etiqueta : el.dataset.vacio;
    });
    document.querySelectorAll('[data-garaje-abrir]').forEach((el) => el.toggleAttribute('data-con-coche', !!coche));
  };

  // badge de compatibilidad: tres estados, y sin dato del producto nunca decimos "no compatible"
  const pintarBadge = (badge, coche) => {
    if (!coche) {
      badge.hidden = true;
      return;
    }
    const marca = `${coche.token}|${badge.dataset.tokens}`;
    if (badge.dataset.listo === marca) return;
    badge.dataset.listo = marca;

    const tokens = badge.dataset.tokens.split(/\s+/).filter(Boolean);
    badge.hidden = false;
    badge.replaceChildren();

    if (tokens.length === 0) {
      const texto = `Hola, ¿este producto (${badge.dataset.producto}) encaja en mi ${coche.etiqueta}?`;
      const enlace = document.createElement('a');
      enlace.href = `https://wa.me/${badge.dataset.whatsapp}?text=${encodeURIComponent(texto)}`;
      enlace.target = '_blank';
      enlace.rel = 'noopener';
      enlace.textContent = 'Comprobar por WhatsApp';
      badge.dataset.estado = 'revisar';
      badge.append(enlace);
    } else if (tokens.includes('universal')) {
      badge.dataset.estado = 'encaja';
      badge.textContent = 'Universal';
    } else if (tokens.includes(coche.token)) {
      badge.dataset.estado = 'encaja';
      badge.textContent = `Encaja con tu ${coche.etiqueta}`;
    } else {
      badge.dataset.estado = 'no';
      badge.textContent = `No compatible con tu ${coche.etiqueta}`;
    }
  };

  const pintarBadges = (raiz = document) => {
    const coche = leer();
    raiz.querySelectorAll('[data-compat-badge]').forEach((b) => pintarBadge(b, coche));
  };

  const pintarTodo = () => {
    pintarPildoras();
    pintarBadges();
  };

  document.addEventListener('garaje:cambio', pintarTodo);
  window.addEventListener('storage', (e) => e.key === CLAVE && pintarTodo());
  pintarTodo();

  // los facets de shopify repintan la rejilla por ajax: hay que pintar los badges nuevos
  let pendiente = false;
  new MutationObserver((mutaciones) => {
    const hayNuevos = mutaciones.some((m) =>
      [...m.addedNodes].some((n) => n.nodeType === 1 && (n.matches?.('[data-compat-badge]') || n.querySelector?.('[data-compat-badge]')))
    );
    if (!hayNuevos || pendiente) return;
    pendiente = true;
    requestAnimationFrame(() => {
      pendiente = false;
      pintarBadges();
    });
  }).observe(document.body, { childList: true, subtree: true });

  window.Garaje = { leer, guardar, urlFiltrada };
})();
